// ── Subscription tier constants ─────────────────────────────────────────────
// Canonical tier identifiers used by the frontend. Must stay in sync with
// apps/api/src/config/tier-capabilities.ts and packages/shared/src/index.ts.

export const TIER_INDIVIDUAL       = 'INDIVIDUAL';
export const TIER_STARTER          = 'STARTER';
export const TIER_GROWTH           = 'GROWTH';
export const TIER_ENTERPRISE       = 'ENTERPRISE';
export const TIER_PLATFORM_PARTNER = 'PLATFORM_PARTNER';
export const TIER_ACADEMIC         = 'ACADEMIC';

/** Stable error code returned by endpoints that a solo-tier tries to use. */
export const ERR_INDIVIDUAL_TIER_SOLO_ONLY = 'INDIVIDUAL_TIER_SOLO_ONLY';

export function isSoloTier(tier: string | null | undefined): boolean {
  return tier === TIER_INDIVIDUAL;
}

/**
 * Returns the onboarding wizard path for a given subscription tier.
 * Used by email-verification and login flows to route tenants into the
 * correct wizard after authentication.
 *
 * STARTER/GROWTH   → 4-step SME wizard (/onboarding/sme)
 * INDIVIDUAL       → solo wizard (/onboarding/individual)
 * PLATFORM_PARTNER → partner onboarding wizard (/partner/onboarding)
 * ACADEMIC         → ACT.A8 (#1709) Q6 fix: academic tenant admin wizard
 *                  (/onboarding/academic-admin). Previously fell through to
 *                  the legacy BYOC/Managed-infra wizard below — a
 *                  pre-existing latent bug (ACADEMIC tier shipped in ACT.A1
 *                  with no branch here), never hit in production because no
 *                  academic TENANT_ADMIN had logged in through this path
 *                  yet (see Sprint-ACT.A8-recon.md §3).
 * ENTERPRISE / unknown → legacy wizard (/onboarding) — enterprise tenants
 *                  are typically provisioned manually but the legacy wizard
 *                  acts as a safe default if they reach this code path.
 */
export function getOnboardingPathForTier(tier: string | null | undefined): string {
  const normalised = (tier ?? '').trim().toUpperCase();
  if (normalised === TIER_STARTER || normalised === TIER_GROWTH) {
    return '/onboarding/sme';
  }
  if (normalised === TIER_INDIVIDUAL) {
    return '/onboarding/individual';
  }
  if (normalised === TIER_PLATFORM_PARTNER) {
    return '/partner/onboarding';
  }
  if (normalised === TIER_ACADEMIC) {
    return '/onboarding/academic-admin';
  }
  return '/onboarding';
}
